import { Injectable } from '@angular/core';
import { NavigationBehaviorOptions } from '@angular/router';
import { Accounts, Commands, Experiences, Results, SessionInfo, Workspace, Permissions, Style } from '@local/client-contracts';
import { AssistantsIconsConst, CreateExperienceResponse, ManualPromise, observable } from '@local/common';
import {
  generateUniqTitle,
  getAssistantDisplayName,
  getAssistantGroupName,
  getAssistantSort,
  getAssistantTitle,
  isChatAssistant,
  isSlackAssistant,
  isTeamsAssistant,
  isTitleEditableAssistant,
} from '@local/common-web';
import { capitalCase, upperCaseWords } from '@local/ts-infra';
import { PopupRef, PopupService } from '@local/ui-infra';
import { DisplaySearchFilterValue, Filter } from '@shared/components/filters/models';
import { DateFormat } from '@shared/consts';
import { EventsService, LogService } from '@shared/services';
import { AccountsService } from '@shared/services/accounts.service';
import { BreadcrumbsService } from '@shared/services/breadcrumbs.service';
import { RouterService } from '@shared/services/router.service';
import { SessionStorageService } from '@shared/services/session-storage.service';
import { SessionService } from '@shared/services/session.service';
import { SlackBotService } from '@shared/services/slack-bot.service';
import { TitleBarService } from '@shared/services/title-bar.service';
import { generateFullPrefixedURL } from '@shared/utils';
import { getTimeFromNowInText } from '@shared/utils/date-format.util';
import { Logger } from '@unleash-tech/js-logger';
import { cloneDeep, invert } from 'lodash';
import moment from 'moment';
import { BehaviorSubject, Observable, combineLatest, distinctUntilChanged, firstValueFrom, take, takeUntil } from 'rxjs';
import { AppPopupComponent, AppPopupData } from '../components/app-popup/app-popup.component';
import {
  ScopePermissionsOptions,
  ShareOptionsPermissionsModel,
} from '../components/share-options-permissions/share-options-permissions.component';
import { AvatarItemModel } from '../models/avatar-item.model';
import { ResultContextMenuItem, TelemetryTrigger } from '../views';
import { assistantContent } from '../views/settings/components/assistant-page/helpers/assistant.content';
import { CommandsService } from './commands/commands.service';
import { HubService } from './hub.service';
import { ShareOptionsService } from './share-options.service';
import { CONTROL_PAGE_PATH } from '../utils/constants';
import { ExperiencesInvokerService } from './experiences-invoker.service';
import { SlackAssistantCreateComponent } from '../views/settings/components/assistant-page/components/assistants/slack-assistant-create-popup/slack-assistant-create-popup.component';
import { BlobsService } from './blobs/blob.service';
import { ShowToasterService } from './show-toaster.service';

export const ASSISTANT_MAPPED_TYPES = {
  'next-gen': 'agentic',
};

@Injectable({ providedIn: 'root' })
export class ExperiencesService {
  readonly ASSISTANT_MAPPED_TYPES = ASSISTANT_MAPPED_TYPES;
  readonly SHARE_OPTIONS_ASSISTANTS: ScopePermissionsOptions[] = [
    {
      label: 'Full access',
      subLabel: 'Edit preferences',
      value: 'write',
    },
    {
      label: 'Limited access',
      subLabel: 'View preferences',
      value: 'read',
    },
  ];
  private logger: Logger;
  private _currentAssistant$ = new BehaviorSubject<Experiences.ExperienceItem>(null);
  private _assistantPageFilters$ = new BehaviorSubject<Filter[]>(null);
  private sessionInfo: SessionInfo;
  private areYouSurePopupRef: PopupRef<AppPopupComponent, AppPopupData> = null;
  private readonly SHARE_OPTIONS_GENERAL_ASSISTANT: ScopePermissionsOptions[] = [
    {
      label: 'Full access',
      subLabel: 'Edit preferences & interact',
      value: 'write',
    },
    {
      label: 'Limited access',
      subLabel: 'View preferences & interact',
      value: 'read',
    },
    {
      label: 'Interact',
      subLabel: 'Can search using the assistant',
      value: 'search',
    },
  ];
  private readonly ARE_YOU_SURE_POPUP: AppPopupData = {
    showButtons: true,
    content: {
      title: 'Are you sure you want to delete <br> this assistant?',
      secondaryButton: 'Cancel',
      primaryButton: 'Delete',
    },
    titleStyle: { 'font-weight': 'normal' },
    messageStyle: { fontSize: '12px' },
  };
  private readonly SHARE_POPUP_MESSAGE = ' This will affect all users it is shared with.';
  private readonly SHARE_POPUP_SLACK_MESSAGE = ' Deleting this assistant will remove Slack assistant functionality for this channel.';
  private readonly ASSISTANTS_LAYOUT_MODE_ENTRY = 'assistantsLayoutMode';
  private readonly DATA_SOURCES_PROPERTIES = ['filtersDataSources', 'modifiedAt', 'modifiedBy'];
  private readonly ASSISTANTS_URL = `${CONTROL_PAGE_PATH}/assistants`;
  private readonly TOASTER_ID = 'file-error';
  private _assistantsLayoutMode$ = new BehaviorSubject<Results.LayoutType>(null);
  private isOwnerOrAdmin: boolean;
  private _disableCreateSlackAssistant$ = new BehaviorSubject<string>(null);
  private _onDeletedAssistantId$ = new BehaviorSubject<string>(null);
  private _slackWorkspaces: Set<string> = new Set();
  private _botNamesByWorkspace: Map<string, Set<string>> = new Map();
  private _sessionReady: ManualPromise<void> = new ManualPromise();

  @observable
  get currentAssistant$(): BehaviorSubject<Experiences.ExperienceItem> {
    return this._currentAssistant$;
  }

  private set currentAssistant(assistant: Experiences.ExperienceItem) {
    this._currentAssistant$.next(assistant);
  }

  get currentAssistant(): Experiences.ExperienceItem {
    return this._currentAssistant$.value;
  }

  @observable
  get disableCreateSlackAssistant$(): Observable<string> {
    return this._disableCreateSlackAssistant$.asObservable();
  }

  @observable
  get assistantPageFilters$(): Observable<Filter[]> {
    return this._assistantPageFilters$.asObservable();
  }

  @observable
  get assistantsLayoutMode$(): Observable<Results.LayoutType> {
    return this._assistantsLayoutMode$.asObservable();
  }

  @observable
  get onDeletedAssistantId$(): Observable<string> {
    return this._onDeletedAssistantId$.asObservable();
  }

  constructor(
    logger: LogService,
    private sessionService: SessionService,
    private accountsService: AccountsService,
    private breadcrumbsService: BreadcrumbsService,
    private eventsService: EventsService,
    private routerService: RouterService,
    private commandsService: CommandsService,
    private titleBarService: TitleBarService,
    private popupService: PopupService,
    private shareOptionsService: ShareOptionsService,
    private sessionStorageService: SessionStorageService,
    private slackBotService: SlackBotService,
    private hubService: HubService,
    private blobsService: BlobsService,
    private showToasterService: ShowToasterService,
    private experiencesInvokerService: ExperiencesInvokerService
  ) {
    this.logger = logger.scope('AssistantService');
    combineLatest([this.experiencesInvokerService.visible$, this.routerService.activeRoute$]).subscribe(([assistants, currentRoute]) => {
      const assistantRoute = currentRoute?.snapshot?.data?.assistant as Experiences.ExperienceItem;

      if (!assistantRoute || !assistants) {
        this.currentAssistant = null;
        return;
      }

      if (!currentRoute) {
        return;
      }

      const updatedAssistant = assistants.find((b) => b.id === assistantRoute.id);

      if (!updatedAssistant) {
        this.currentAssistant = null;
        return;
      }

      const breadcrumbIcon = AssistantsIconsConst[updatedAssistant.experienceType] as Style.EntityIcon<Style.EntityIconType>;
      this.breadcrumbsService.items = [
        {
          title: 'Assistants',
          path: 'assistants',
          icon: {
            type: 'font-icon',
            value: 'icon-assistant',
          },
        },
        {
          title: getAssistantTitle(updatedAssistant),
          path: updatedAssistant.id,
          icon: isChatAssistant(updatedAssistant) ? null : breadcrumbIcon,
        },
      ];

      const assistantChanged = this.currentAssistant?.id !== updatedAssistant?.id;
      if (assistantChanged || this.currentAssistant?.permissionRole !== updatedAssistant?.permissionRole) {
        this.currentAssistant = { ...updatedAssistant };
        this.titleBarService.active = getAssistantTitle(updatedAssistant);
      }

      if (this.currentAssistant?.isPartial !== updatedAssistant?.isPartial) {
        this.currentAssistant = {
          ...this.currentAssistant,
          dataSources: updatedAssistant.dataSources,
          filtersDataSources: updatedAssistant.filtersDataSources,
          channelDataSource: updatedAssistant.channelDataSource,
          isPartial: updatedAssistant.isPartial,
        };
      }

      if (!this.shareOptionsService.compareShareOptions(this.currentAssistant.shareOptions, updatedAssistant?.shareOptions)) {
        this.currentAssistant = {
          ...this.currentAssistant,
          shareOptions: updatedAssistant.shareOptions,
          isShared: updatedAssistant.isShared,
          effectiveScopes: updatedAssistant.effectiveScopes,
          modifiedAt: updatedAssistant.modifiedAt,
          modifiedBy: updatedAssistant.modifiedBy,
        };
      }
    });

    this.initSession();
    this.initAssistantPageFilters();

    this.sessionService.accountChanged$.subscribe((s) => {
      if (!s) {
        this._assistantsLayoutMode$.next('list');
      } else {
        this.loadAssistantsLayoutModeRoaming();
      }
    });
  }

  get workspace(): Workspace.Workspace {
    return this.sessionInfo?.workspace;
  }

  get slackWorkspaces(): Set<string> {
    return this._slackWorkspaces;
  }

  get botNamesByWorkspace(): Map<string, Set<string>> {
    return this._botNamesByWorkspace;
  }

  getExperience$(id: string, cache?: Experiences.CacheType): Observable<Experiences.ExperienceItem> {
    return this.experiencesInvokerService.getExperience$(id, cache);
  }

  private getReverseMappedAssistantType(assistantType: string): string | undefined {
    const reversedMapping = invert(this.ASSISTANT_MAPPED_TYPES);
    return reversedMapping[assistantType] || assistantType;
  }

  private initSession(): void {
    combineLatest([this.sessionService.current$, this.accountsService.all$]).subscribe({
      next: ([sessionInfo, accounts]) => {
        if (!sessionInfo) {
          return;
        }
        this.sessionInfo = cloneDeep(sessionInfo);
        if (this._sessionReady.status !== 'resolved') {
          this._sessionReady.resolve();
        }
        if (this.sessionInfo.workspace) {
          this.sessionInfo.workspace.accounts = accounts;
        }
        this.isOwnerOrAdmin = this.workspace?.isAdmin || this.workspace?.isOwner;
      },
    });
  }

  async initSlackBotInstallInfo() {
    if (!(await this.checkAssistantsFlags('slack'))) {
      this._disableCreateSlackAssistant$.next(null);
      return;
    }

    const installations = await this.slackBotService.getSlackInstallations();

    if (!installations.installed) {
      this._disableCreateSlackAssistant$.next('Unleash app must be installed to create Assistants');
      return;
    }
    if (this.isOwnerOrAdmin) {
      this._disableCreateSlackAssistant$.next(null);
      return;
    }

    const hasAllowedInstallation = installations?.installations?.some((installation) => installation.allowMemberToCreateAssistant === true);

    if (!hasAllowedInstallation) {
      this._disableCreateSlackAssistant$.next('Contact your admin');
      return;
    }
    this._disableCreateSlackAssistant$.next(null);
  }

  async checkAssistantsFlags(assistantType: string) {
    await this._sessionReady;
    const assistantSetting = this.workspace?.settings.assistant?.settings[assistantType.toLowerCase()];
    return assistantSetting?.enabled;
  }

  isOwner(assistant: Experiences.ExperienceItem): boolean {
    return assistant?.createdBy === this.workspace?.accountId;
  }

  isLastModifiedByCurrentUser(assistant: Experiences.ExperienceItem): boolean {
    return assistant?.modifiedBy === this.workspace?.accountId;
  }

  private async loadAssistantsLayoutModeRoaming() {
    return this.sessionStorageService
      .getStore('roaming', 'account')
      .entry<Results.LayoutType>(this.ASSISTANTS_LAYOUT_MODE_ENTRY)
      .get()
      .then((layout: Results.LayoutType) => {
        this._assistantsLayoutMode$.next(layout);
      });
  }

  async saveAssistantsLayoutModeRoaming(layoutMode: Results.LayoutType) {
    this.sessionStorageService
      .getStore('roaming', 'account')
      .entry<Results.LayoutType>(this.ASSISTANTS_LAYOUT_MODE_ENTRY)
      .set(layoutMode)
      .then(() => {
        this._assistantsLayoutMode$.next(layoutMode);
      });
  }

  //shared functions
  getLastModified(assistant: Experiences.ExperienceItem): AvatarItemModel {
    const user = this.isLastModifiedByCurrentUser(assistant)
      ? this.sessionInfo?.user
      : this.workspace?.accounts?.find((a) => a.id === assistant?.modifiedBy);
    const name = `${upperCaseWords(user?.name || '')}`;
    return { name, imgUrl: user?.picture, email: user?.email, isNameFromEmail: user?.isNameFromEmail };
  }

  getAssistantTimePhrases(assistant: Experiences.ExperienceItem) {
    const displayModifiedTime = getTimeFromNowInText(assistant.modifiedAt).toLowerCase();
    const preDisplayModifiedTime =
      assistant.modifiedAt === assistant.createdAt
        ? capitalCase(assistantContent.created) + ' '
        : capitalCase(assistantContent.updated) + ' ';
    let fullDetails = moment(assistant.modifiedAt).format(DateFormat.FULL_DATE_HOURS_A);
    let startModifiedPhrase: string;
    if (this.isLastModifiedByCurrentUser(assistant)) {
      startModifiedPhrase = 'You';
    } else {
      const user = this.getLastModified(assistant);
      startModifiedPhrase = user.name;
      if (user.isNameFromEmail) {
        fullDetails = `${user.email} - ${fullDetails}`;
      }
    }
    return {
      displayModifiedTime,
      preDisplayModifiedTime,
      fullDetails,
      startModifiedPhrase,
    };
  }

  async onContextMenuCommand(
    item: ResultContextMenuItem,
    trigger: TelemetryTrigger,
    assistant: Experiences.ExperienceItem,
    currentLocation: string
  ) {
    this.eventsService.event('assistant.assistant_actions', {
      target: item.id,
      label: trigger === 'context_menu_click' ? 'mouse_click' : 'keyboard',
      location: { title: currentLocation },
    });

    this.eventsService.event('assistant.menu_action', {
      label: item.id,
      location: { title: currentLocation },
    });

    switch (item.command.type) {
      case 'open-assistant':
        this.openAssistant(assistant);
        break;
      case 'copy-url':
        this.copyUrl(assistant);
        break;
      case 'delete':
        return this.deleteAssistant(assistant);
      case 'share-assistant':
        this.shareAssistant(assistant);
        break;
      case 'copy-id':
        this.copyAssistantId(assistant.id);
        break;
    }
  }

  private copyAssistantId(assistantId: string) {
    this.commandsService.executeCommand({
      type: 'copy-clipboard',
      value: assistantId,
    } as Commands.CopyClipboard);
  }

  async buildTitle(id: string, title: string, experienceType?: Experiences.ExperienceType): Promise<string> {
    const allAssistant = await firstValueFrom(this.experiencesInvokerService.visible$);
    const current = allAssistant.find((b) => b.id === id);
    if (current && isTitleEditableAssistant(current) && current?.settings?.title === title) {
      return title;
    }
    const titles: string[] = allAssistant
      .filter((as) => as.experienceType === experienceType)
      .map((assistant) => getAssistantTitle(assistant));
    return generateUniqTitle(title, titles);
  }

  async openAssistant(assistant: Experiences.ExperienceItem, extras?: NavigationBehaviorOptions) {
    const url = `/${CONTROL_PAGE_PATH}/assistant-settings/${assistant.id}`;
    this.hubService.openUrl(url, extras);
  }

  async createAssistant(
    assistant?: Experiences.ExperienceItemData,
    unavailableFilterSourceIds?: number[],
    replaceUrl = false,
    openInNewTab = false
  ): Promise<string> {
    const { experienceType } = assistant;
    const updatedAssistant = cloneDeep(assistant);
    this.breadcrumbsService.showGhost = true;
    if (isTitleEditableAssistant(updatedAssistant)) {
      const title = updatedAssistant.settings?.title ?? (await this.buildTitle(null, 'Untitled', experienceType));
      updatedAssistant.settings = { ...(updatedAssistant?.settings || {}), title };
    }
    let response: CreateExperienceResponse;
    try {
      response = await this.experiencesInvokerService.create(experienceType, { ...updatedAssistant }, unavailableFilterSourceIds);
      this.openAssistant(response as any, { state: { mode: 'new' }, replaceUrl });
    } catch (error) {
      this.routerService.navigateByUrl(`${CONTROL_PAGE_PATH}/assistants`, { replaceUrl }, true, openInNewTab);
      this.logger.error('failed to create assistant', error);
    }
    this.breadcrumbsService.showGhost = false;
    return response?.id;
  }

  async createDraftAssistant(experienceType: Experiences.ExperienceType, extras?: NavigationBehaviorOptions): Promise<void> {
    this.breadcrumbsService.showGhost = true;
    if (experienceType === 'slack') {
      this.popupService.open<SlackAssistantCreateComponent, any>('center', SlackAssistantCreateComponent, null, {
        closeOnClickOut: true,
        panelClass: 'slack-assistant-create-popup',
        backdropStyle: 'blur-2',
      });
    } else {
      const type = this.getReverseMappedAssistantType(experienceType);
      this.hubService.openUrl(`${CONTROL_PAGE_PATH}/assistant-settings/new/${type}`, extras);
    }
  }

  async getAssistantTitle(assistant: Experiences.ExperienceItem, defaultTitle: string): Promise<string> {
    if (isTitleEditableAssistant(assistant)) {
      const { experienceType } = assistant;
      const assistantTitle = assistant?.settings?.title;
      return assistantTitle && assistantTitle !== defaultTitle ? assistantTitle : await this.buildTitle(null, defaultTitle, experienceType);
    }
    return null;
  }

  async saveDraftAssistant(
    assistant: Experiences.ExperienceItem,
    unavailableFilterSourceIds?: number[],
    replaceUrl = false,
    defaultTitle = 'Untitled'
  ) {
    this.breadcrumbsService.showGhost = true;
    const { experienceType } = assistant;
    let title;
    if (isTitleEditableAssistant(assistant)) {
      title = await this.getAssistantTitle(assistant, defaultTitle);
      assistant.settings.title = title;
    }
    try {
      const response = await this.experiencesInvokerService.create(experienceType, assistant, unavailableFilterSourceIds);
      const url = `${CONTROL_PAGE_PATH}/assistant-settings/${response.id}`;
      this.routerService.navigateByUrl(url, { replaceUrl });
      this.breadcrumbsService.showGhost = false;
      return { title, success: true, response };
    } catch (error) {
      this.routerService.navigateByUrl(`${CONTROL_PAGE_PATH}/assistants`, { replaceUrl });
      this.logger.error('failed to create assistant', error);
      this.breadcrumbsService.showGhost = false;
      return { title: 'Failed to create assistant', success: false };
    }
  }

  deleteAssistant(assistant: Experiences.ExperienceItem, navigate?: boolean) {
    this._onDeletedAssistantId$.next(assistant.id);
    return this.openAreYouSurePopup(assistant, navigate);
  }

  private getCancelPopupMessage(experienceType: Experiences.ExperienceType): string {
    return experienceType === 'slack' ? this.SHARE_POPUP_SLACK_MESSAGE : this.SHARE_POPUP_MESSAGE;
  }

  private openAreYouSurePopup(assistant: Experiences.ExperienceItem, navigate?: boolean) {
    if (this.areYouSurePopupRef) {
      this.areYouSurePopupRef.destroy();
    }
    const currentMessage = this.getCancelPopupMessage(assistant.experienceType);
    this.areYouSurePopupRef = this.popupService.open<AppPopupComponent, AppPopupData>(
      'center',
      AppPopupComponent,
      assistant.isShared ? { ...this.ARE_YOU_SURE_POPUP, message: currentMessage, titleStyle: {} } : this.ARE_YOU_SURE_POPUP
    );
    this.areYouSurePopupRef.compInstance.primaryButton.pipe(takeUntil(this.areYouSurePopupRef.destroy$)).subscribe(async () => {
      if (navigate) {
        this.hubService.openUrl(this.ASSISTANTS_URL);
      }
      await this.experiencesInvokerService.delete(assistant.id);
    });
    this.areYouSurePopupRef.destroy$.pipe(take(1)).subscribe(() => {
      this.areYouSurePopupRef = null;
    });
    return this.areYouSurePopupRef;
  }

  shareAssistant(experience: Experiences.ExperienceItem, isNew?: boolean) {
    const updatedExperience = isNew ? experience : this.experiencesInvokerService.allExperiences?.find((exp) => exp.id === experience.id);
    if (!updatedExperience) {
      return;
    }
    const title = getAssistantTitle(updatedExperience);
    const writePermissions = experience.shareOptions?.permissions?.find((p) => p.scope === 'write');
    const hasRolePermission = writePermissions?.roles?.length;
    const shareOptionsModel: ShareOptionsPermissionsModel = {
      accountId: experience.createdBy,
      isShared: experience.isShared,
      shareOptions: experience.shareOptions,
      title: isNew ? 'Set access for your assistant' : `Share "${title}" Assistant`,
      disclaimer: isNew
        ? 'Shared users can interact with this assistant in Chat. Full or limited access also allows viewing or editing its settings.'
        : null,
      displayPermissionOption: true,
      selectedOwnerOrAdminByRole: hasRolePermission
        ? {
            enabled: true,
            label: 'Full access',
            updateRolesWhenSave: true,
          }
        : null,
      telemetryName: 'assistant_share',
      isOwner: this.isOwner(experience),
      inputPlaceholder: 'Name or email',
      scopePermissionOptions: isChatAssistant(experience) ? this.SHARE_OPTIONS_GENERAL_ASSISTANT : this.SHARE_OPTIONS_ASSISTANTS,
      defaultScopePermission: isChatAssistant(experience) ? 'search' : 'read',
    };
    this.shareOptionsService.openShareOptionPopup(shareOptionsModel, async (target, shareOptions) => {
      await this.experiencesInvokerService.updateShareOptions(experience.id, shareOptions, experience.experienceType);
    });
  }

  getSharedObjects(accountId: string): Promise<Experiences.ExperienceSharedObjects> {
    return this.experiencesInvokerService.getSharedObjects(accountId);
  }

  copyUrl(assistant: Experiences.ExperienceItem) {
    this.commandsService.executeCommand({
      type: 'copy-clipboard',
      value: generateFullPrefixedURL(`/assistant-settings/${assistant.id}`, 'path'),
    } as Commands.CopyClipboard);
  }

  //@@@@@@@FILTERS

  initAssistantPageFilters(typeFilter?: string) {
    combineLatest([
      this.experiencesInvokerService.visible$,
      this.accountsService.all$.pipe(distinctUntilChanged()),
      this.sessionService.current$,
    ]).subscribe(async ([assistants, accounts, sessionInfo]) => {
      if (!assistants) return;
      if (!sessionInfo) return;

      const createdByMap = new Set();
      const typesMap = new Map();
      const teamsMap = new Set<string>();

      assistants.forEach((c) => {
        if (!createdByMap.has(c.createdBy)) {
          if (!typeFilter) {
            createdByMap.add(c.createdBy);
          } else if (typeFilter === c.experienceType) {
            createdByMap.add(c.createdBy);
          }
        }
        if (!typesMap.has(c.experienceType)) {
          typesMap.set(c.experienceType, getAssistantGroupName(c.experienceType));
        }
        if (isTeamsAssistant(c)) {
          const teamName = c.settings?.teamName;
          if (teamName && !teamsMap.has(teamName)) {
            teamsMap.add(teamName);
          }
        }
        if (isSlackAssistant(c)) {
          const workspace = c.settings?.workspaceName;
          const botName = c.settings?.botName;

          if (workspace && botName) {
            if (!this._botNamesByWorkspace.has(workspace)) {
              this._botNamesByWorkspace.set(workspace, new Set());
            }
            this._botNamesByWorkspace.get(workspace)!.add(botName);
          }
        }
      });

      let meElement = null;
      let isNotMeAdded = false;
      const createdBy = [];
      [...createdByMap.keys()].forEach((c) => {
        const elm = accounts?.find((w) => w.id === c);
        if (elm) {
          if (elm?.userId !== sessionInfo?.user.id) {
            if (!isNotMeAdded) {
              createdBy.unshift({
                id: 'shared-with-me',
                name: 'Not me',
              });
              isNotMeAdded = true;
            }
            createdBy.push(elm);
          } else if (elm?.userId === sessionInfo?.user.id) {
            meElement = { ...elm };
            meElement.name = 'Me';
          }
        }
      });
      if (meElement) {
        createdBy.unshift(meElement);
      }

      const filters: Filter[] = await this.createCollectionPageFilters(createdBy, [...typesMap.values()], [...teamsMap]);
      this._assistantPageFilters$.next(filters);
    });
  }

  async createCollectionPageFilters(createdBy: Accounts.WorkspaceAccount[], types: string[], teams: string[]): Promise<Filter[]> {
    const filters: Filter[] = [];

    const createdByName = 'assistant-createdBy';
    filters.push({
      type: 'post',
      name: createdByName,
      title: 'Created by',
      icon: { type: 'font-icon', value: 'icon-user-circle' },
      picker: 'multi-select',
      viewDetails: {
        isTwoLine: true,
        showClearAll: true,
      },
      sort: 'none',
      values: createdBy?.map(
        (value) =>
          <DisplaySearchFilterValue>{
            id: value?.id,
            value: value?.id,
            title: value?.name,
            subtitle: value?.email || '',
            filterName: createdByName,
          }
      ),
      disabled: false,
    });

    types = types?.sort((a, b) => {
      return getAssistantSort(a, b);
    });

    const typesName = 'assistant-type';
    filters.push({
      type: 'post',
      name: typesName,
      title: 'Type',
      icon: { type: 'font-icon', value: 'icon-assistant' },
      picker: 'multi-select',
      values: types.map(
        (value) =>
          <DisplaySearchFilterValue>{
            id: value,
            value: value,
            title: getAssistantDisplayName(value.toLowerCase() as Experiences.ExperienceType),
            subtitle: null,
            filterName: typesName,
            icon: AssistantsIconsConst[getAssistantGroupName(value, 'lower')],
          }
      ),
      disabled: false,
      viewDetails: {
        showItemIcon: true,
        isFullDetailLine: true,
        showSelectedItemIcons: true,
        oneValue: true,
        noCheckbox: true,
      },
    });

    const teamName = 'assistant-team';
    filters.push({
      type: 'post',
      name: teamName,
      title: 'Team',
      icon: { type: 'font-icon', value: 'icon-team' },
      picker: 'multi-select',
      values: teams.map(
        (value) =>
          <DisplaySearchFilterValue>{
            id: value,
            value: value,
            title: value,
            subtitle: null,
            filterName: teamName,
          }
      ),
      disabled: false,
      viewDetails: {
        showItemIcon: true,
        isFullDetailLine: true,
        showSelectedItemIcons: true,
      },
    });

    if (this.botNamesByWorkspace.size > 1) {
      const workspaceName = 'assistant-slack-workspace';
      filters.push({
        type: 'post',
        name: workspaceName,
        title: 'Workspace',
        icon: { type: 'font-icon', value: 'icon-buildings' },
        picker: 'multi-select',
        values: [...this.botNamesByWorkspace.keys()].map(
          (value) =>
            <DisplaySearchFilterValue>{
              id: value,
              value: value,
              title: value,
              subtitle: null,
              filterName: workspaceName,
            }
        ),
        disabled: false,
        viewDetails: {
          showItemIcon: true,
          isFullDetailLine: true,
          showSelectedItemIcons: true,
        },
      });
    }

    return filters;
  }

  async uploadFile(file: File): Promise<string> {
    const { name, type } = file;
    const errorMessage = `Upload of file "${name}" failed`;
    const shareOptions: Permissions.ShareOptions = {
      level: 'Protected',
      permissions: [
        { scope: 'read', workspaceIds: [this.workspace?.id] },
        { scope: 'write', workspaceIds: [this.workspace?.id] },
        { scope: 'search', workspaceIds: [this.workspace?.id] },
      ],
    };
    try {
      const blobId = (await this.blobsService.create(name, type, shareOptions))?.id;
      const uploadResponse = await this.blobsService.upload(file, blobId);
      if (uploadResponse.status >= 400) {
        this.onFileUploadError(errorMessage);
        return;
      }
      return blobId;
    } catch {
      this.onFileUploadError(errorMessage);
    }
  }

  onFileUploadError(message: string) {
    this.showToasterService.showErrorToaster(this.TOASTER_ID, message);
  }

  async initFile(blobId: string): Promise<string> {
    return await this.blobsService.getBlobFullUrl(blobId);
  }

  async deleteFile(blobId: string): Promise<void> {
    await this.blobsService.delete({ ids: [blobId] });
  }
}
